import { useMemo, type FC, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@r/components/ui/form.tsx';
import { Input } from '@r/components/ui/input.tsx';
import { Button } from '@r/components/ui/button.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@r/components/ui/select.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import type { TargetLogicType } from '@admin/types/workspace/logic.ts';
import { LOGIC_TRANSLATION_KEYS } from '@admin/types/workspace/logic.ts';
import { LogicLogicReadModelType } from '@r/api/generated/client.schemas.ts';

const createFormSchema = (t: ReturnType<typeof useTranslation>['t'], fixedType?: TargetLogicType) =>
  z.object({
    reference: z
      .string()
      .min(1, t('form.target.error.reference.min'))
      .max(20, t('form.target.error.reference.max')),
    description: z.string().optional(),
    logicTargetType: fixedType
      ? z.literal(fixedType)
      : z.enum([LogicLogicReadModelType.TARGET_POINT, LogicLogicReadModelType.TARGET_PERF], {
          required_error: t('form.target.type.required')
        })
  });

interface TargetFormProps {
  onSubmitTarget: (target: z.infer<ReturnType<typeof createFormSchema>>) => void;
  fixedType?: TargetLogicType;
}

export const TargetForm: FC<TargetFormProps> = ({ onSubmitTarget, fixedType }) => {
  const { t } = useTranslation();

  const schema = createFormSchema(t, fixedType);

  const logicTranslations = useMemo(() => {
    return {
      [LogicLogicReadModelType.TARGET_PERF]: t(
        LOGIC_TRANSLATION_KEYS[LogicLogicReadModelType.TARGET_PERF]
      ),
      [LogicLogicReadModelType.TARGET_POINT]: t(
        LOGIC_TRANSLATION_KEYS[LogicLogicReadModelType.TARGET_POINT]
      )
    };
  }, [t]);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      reference: '',
      description: '',
      logicTargetType: fixedType
    }
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit((data) => {
      onSubmitTarget(data);
    })(e);
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="reference"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('form.target.reference.new.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('form.target.reference.new.label.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('form.target.description.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('form.target.description.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="logicTargetType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('form.target.type.label')}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value} disabled={!!fixedType}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('form.target.type.placeholder')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={LogicLogicReadModelType.TARGET_POINT}>
                      {logicTranslations[LogicLogicReadModelType.TARGET_POINT]}
                    </SelectItem>
                    <SelectItem value={LogicLogicReadModelType.TARGET_PERF}>
                      {logicTranslations[LogicLogicReadModelType.TARGET_PERF]}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {fixedType && (
                  <p className="text-sm text-muted-foreground">
                    {t('form.target.type.fixed.info')}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <Button className="w-full" type="submit">
            {t('form.target.submit')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
